import { Controller } from '@hotwired/stimulus'
import fetchWithHandlingLoginRedirect from '../../modules/utils/fetchWithHandlingLoginRedirect'
import { trans, WEBHOOKS_TEST_SUCCESS, WEBHOOKS_TEST_FAILED_WITH_RESPONSE, WEBHOOKS_TEST_FAILED_CONNECTION } from '../../translator'

interface WebhookTestDeliveryResult {
  success: boolean
  response_code: number | null
}

const JSON_HEADERS = { Accept: 'application/json' }

/**
 * Sends a synchronous test event for a webhook subscription and flashes the result in the
 * full-width page alert, per tech-spec-outbound-webhooks.md Section 10.1/10.4. One controller
 * instance covers every row's "Send test event" action, the same way save-webhook-modal covers
 * enable/disable/delete.
 */
export default class extends Controller {
  static values = {
    baseUrl: String
  }

  static targets = ['alert', 'text']

  declare baseUrlValue: string
  declare readonly alertTarget: HTMLElement
  declare readonly textTarget: HTMLElement

  async send (event: Event & { params: { id: string, url: string }, currentTarget: HTMLElement }): Promise<void> {
    const trigger = event.currentTarget
    if (trigger.classList.contains('disabled')) {
      return
    }

    const { id, url } = event.params
    trigger.classList.add('disabled')

    let result: WebhookTestDeliveryResult
    try {
      const response = await fetchWithHandlingLoginRedirect(`${this.baseUrlValue}/${id}/test`, {
        method: 'POST',
        headers: JSON_HEADERS
      })
      const body = await response.json() as { result: WebhookTestDeliveryResult }
      result = body.result
    } catch {
      result = { success: false, response_code: null }
    }

    trigger.classList.remove('disabled')
    this.notifyResult(url, result)
  }

  dismiss (): void {
    this.hide()
  }

  private notifyResult (url: string, result: WebhookTestDeliveryResult): void {
    let message: string
    if (result.success) {
      message = trans(WEBHOOKS_TEST_SUCCESS, { '%url%': url, '%response_code%': String(result.response_code) }, 'integrations')
    } else if (result.response_code !== null) {
      message = trans(WEBHOOKS_TEST_FAILED_WITH_RESPONSE, { '%url%': url, '%response_code%': String(result.response_code) }, 'integrations')
    } else {
      message = trans(WEBHOOKS_TEST_FAILED_CONNECTION, { '%url%': url }, 'integrations')
    }

    this.textTarget.textContent = message
    this.alertTarget.classList.remove('alert-success', 'alert-danger', 'd-none')
    this.alertTarget.classList.add(result.success ? 'alert-success' : 'alert-danger')
  }

  private hide (): void {
    this.textTarget.textContent = ''
    this.alertTarget.classList.remove('alert-success', 'alert-danger')
    this.alertTarget.classList.add('d-none')
  }
}
